import { supabase } from '../supabaseClient';

// Simulação de chamadas ao backend/edge functions para operações sensíveis
// Em produção, isso deve ser feito via Edge Functions para não expor lógica sensível

export const AffiliateService = {
  // Verificar status de afiliado do usuário
  async getAffiliateStatus(userId) {
    if (!userId) return null;
    
    const { data, error } = await supabase
      .from('affiliates')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 é "não encontrado"
      console.error('Erro ao buscar afiliado:', error);
      return null;
    }
    return data;
  },

  // Criar perfil de afiliado
  async createAffiliateProfile(userId, userName) {
    // Gera um código baseada no nome ou aleatório
    const baseCode = userName ? userName.split(' ')[0].toUpperCase() : 'PARCEIRO';
    const randomSuffix = Math.floor(1000 + Math.random() * 9000);
    const referralCode = `${baseCode}${randomSuffix}`;

    const { data, error } = await supabase
      .from('affiliates')
      .insert([
        { 
          user_id: userId, 
          referral_code: referralCode,
          balance_available: 0,
          balance_pending: 0,
          total_earnings: 0,
          approval_status: 'pending',
          pix_verification_status: 'pending'
        }
      ])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Obter comissões
  async getCommissions(affiliateId) {
    const { data, error } = await supabase
      .from('commissions')
      .select(`
        *,
        referrals (
           referred_user_id
        )
      `)
      .eq('affiliate_id', affiliateId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Obter saques
  async getPayouts(affiliateId) {
    const { data, error } = await supabase
      .from('payouts')
      .select('*')
      .eq('affiliate_id', affiliateId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async getClicks(affiliateId, limit = 20) {
    const { data, error } = await supabase
      .from('affiliate_clicks')
      .select('*')
      .eq('affiliate_id', affiliateId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return data;
  },

  // Iniciar fluxo de conexão com Stripe (Connect)
  async connectStripe(affiliateId) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) throw new Error('Usuário não autenticado');

    // Chama a Edge Function para gerar o link de onboarding do Stripe
    const { data, error } = await supabase.functions.invoke('stripe-connect', {
      body: { affiliateId }
    });

    if (error) {
      console.error('Erro na Edge Function stripe-connect:', error);
      // Tentar extrair mais detalhes do erro se possível
      if (error instanceof Error && error.message.includes('401')) {
         console.error('Possível causa: Variáveis de ambiente (STRIPE_SECRET_KEY) não configuradas ou erro de autenticação.');
      }
      throw error;
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    return {
      url: data.url,
      mocked: false
    };
  },

  // Solicitar saque
  async requestPayout(affiliateId, amount) {
    if (amount <= 0) throw new Error('Valor inválido');
    const BAAS_API_URL = window.__BAAS_API_URL__ || '/api';
    const res = await fetch(`${BAAS_API_URL}/payouts/request.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ affiliateId, amount })
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Erro ao solicitar saque');
    return result?.data || result;
  },

  // Rastrear clique (público)
  async trackClick(code) {
    if (!code) return;
    try {
      await supabase.rpc('track_affiliate_click', {
        code: code,
        ip: null, // Opcional, pegaria via Edge Function se fosse crítico
        ua: navigator.userAgent,
        ref_url: document.referrer
      });
    } catch (e) {
      console.error('Erro tracking:', e); // Falha silenciosa para não atrapalhar UX
    }
  },

  // Atualizar código de afiliado
  async updateReferralCode(newCode) {
    const { data, error } = await supabase.rpc('update_affiliate_code', {
      new_code: newCode
    });
    
    if (error) throw error;
    if (!data.success) throw new Error(data.message);
    
    return data;
  },

  // Atualizar configurações de pagamento
  async updatePayoutSettings(affiliateId, minAmount, schedule) {
    const { data, error } = await supabase
      .from('affiliates')
      .update({ 
        min_payout_amount: minAmount,
        payout_schedule: schedule
      })
      .eq('id', affiliateId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Atualizar dados de PIX manual e perfil do parceiro
  async updatePixSettings(affiliateId, { legal_name, cpf_cnpj, instagram, pix_key }) {
    const { data, error } = await supabase
      .from('affiliates')
      .update({ legal_name, cpf_cnpj, instagram, pix_key })
      .eq('id', affiliateId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async validatePixKey({ userId, affiliateId, pix_key, pix_key_type }) {
    const { data, error } = await supabase.functions.invoke('pix-validate', {
      body: { userId, affiliateId, pix_key, pix_key_type }
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  },

  // Atualizar apenas a chave PIX e seu tipo
  async updatePixKey(affiliateId, { pix_key, pix_key_type }) {
    const { data, error } = await supabase
      .from('affiliates')
      .update({ pix_key, pix_key_type })
      .eq('id', affiliateId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async requestSupportPayoutApproval(affiliateId) {
    const { data, error } = await supabase
      .from('affiliates')
      .update({ support_payout_requested: true, support_payout_requested_at: new Date().toISOString() })
      .eq('id', affiliateId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Iniciar verificação PIX (gera registro e marca como pendente)
  async initiatePixVerification(affiliateId, amount = 0.30) {
    const { error: insertError } = await supabase
      .from('pix_verifications')
      .insert([{ affiliate_id: affiliateId, amount, status: 'pending' }]);
    if (insertError) throw insertError;

    const { error: updateError } = await supabase
      .from('affiliates')
      .update({ pix_verification_status: 'pending', pix_verification_amount: amount, pix_verification_started_at: new Date().toISOString() })
      .eq('id', affiliateId);
    if (updateError) throw updateError;

    return { ok: true };
  },

  // Aprovação manual pelo administrador
  async adminApproveAffiliate(affiliateId) {
    const { data, error } = await supabase
      .from('affiliates')
      .update({ approval_status: 'approved', pix_verification_status: 'verified' })
      .eq('id', affiliateId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async adminRejectAffiliate(affiliateId) {
    const { data, error } = await supabase
      .from('affiliates')
      .update({ approval_status: 'rejected', pix_verification_status: 'failed' })
      .eq('id', affiliateId)
      .select()
      .single();
    if (error) throw error;
    return data;
  }
};
